// frontend-sahara/src/pages/visa/VisaRequestForm.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaHandPeace,
  FaCat,
  FaChessKnight,
  FaChess,
  FaHandsPraying,
  FaChargingStation,
  FaUserCheck,
  FaCrow,
} from "react-icons/fa6";
import { GiCoffeeCup } from "react-icons/gi";
import { ToastContainer, toast } from "react-toastify";
import { Form, Button, Container, Table, Card, Row, Col } from "react-bootstrap";
import centers from "../conts/centers.json";
import "bootstrap/dist/css/bootstrap.min.css";
import Modal from "react-modal";
import moment from "moment";
import Select from "react-select";
import visaApi from "../apis/Visa.js";
import { customStyles } from "../assets/css/customStyle.js";
import SendEmail from "../components/sendEmail.js";

const MyVisaScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const visaTypeOptions = [
    { value: "ILR", label: "ILR" },
    { value: "Naturalisation", label: "Naturalisation" },
    { value: "2.Extension", label: "2.Extension" },
    { value: "1.Extension", label: "1.Extension" },
  ];
  const [center, setCenter] = useState("");
  const [centerLabel, setCenterLabel] = useState("");
  const [visaType, setVisaType] = useState("ILR");
  const [onlineSubmissionDate, setOnlineSubmissionDate] = useState("");
  const [biometricDate, setBiometricDate] = useState("");
  const [confirmationDate, setConfirmationDate] = useState("");
  const [visaIssueDate, setVisaIssueDate] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConfirmed, setGdprConfirmed] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [notes, setNotes] = useState("");
  const [visaRows, setVisaRows] = useState([]);
  const [centerOptions, setCenterOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [newVisa, setNewVisa] = useState(false);
  const [showSendEmailToDeveloper, setShowSendEmailToDeveloper] = useState(false);
  const [showTableView, setShowTableView] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const getMasterData = async () => {
    const response = await visaApi.getVisaRecords({ Center: center, VisaType: visaType });
    if (response) {
      setVisaRows(response);
    }
  };
  const handleFetch = async () => {
    const response = await visaApi.getVisaRecords({ Center: center, VisaType: visaType });
    if (response) {
      setVisaRows(response);
      setEmail("");
      setGdprConfirmed(false);
      setSendEmail(false);
      setNotes("");
      setVisaIssueDate("");
      setConfirmationDate("");
      setBiometricDate("");
      setOnlineSubmissionDate("");
      setNewVisa(false);
    }
  };

  useEffect(() => {
    getMasterData();
    setCenterOptions(
      centers.map((center) => ({
        value: center.ci_id,
        label: `${center.issuer_center_id}-${center.issuer_center_name}-${center.availability}`,
      }))
    );
    const visited = localStorage.getItem("visa-app");

    if (!visited) {
      toast.info(t("visa.welcomeMessage"));
      localStorage.setItem("visa-app", "visited");
    }
  }, []);

  const sortedVisaRows = useMemo(() => {
    if (visaRows && visaRows.length) {
      let sortableVisaRows = [...visaRows];
      if (sortConfig.key !== null) {
        sortableVisaRows.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
      return sortableVisaRows;
    }
    return [];
  }, [visaRows, sortConfig]);

  const getWithPercentage = () => {
    const width = window.innerHeight;
    return width < 700 ? "90%" : width < 1000 ? "70%" : "60%";
  };

  const handleSubmit = async (e) => {
    try {
      setInProgress(true);
      if (
        center == "" ||
        visaType == "" ||
        onlineSubmissionDate == "" ||
        biometricDate == "" ||
        gdprConfirmed == false
      ) {
        toast.error("Please fill all the required fields");
        return;
      }

      // Convert dates to moment objects for comparison
      const onlineDate = moment(onlineSubmissionDate);
      const biometricDay = moment(biometricDate);
      const confirmationDay = confirmationDate ? moment(confirmationDate) : null;

      // Validate online submission date is not after biometric date
      if (onlineDate.isAfter(biometricDay)) {
        toast.error("Online submission date cannot be after biometric date");
        return;
      }

      // Validate biometric date is not after confirmation date (if confirmation date exists)
      if (confirmationDay && biometricDay.isAfter(confirmationDay)) {
        toast.error("Biometric date cannot be after confirmation date");
        return;
      }

      if (sendEmail && email == "") {
        toast.error("Please fill the email field to get confirmation updates, or uncheck the send email checkbox");
        return;
      }

      // Collect device and browser information
      const deviceInfo = {
        userAgent: window.navigator.userAgent,
        platform: window.navigator.platform,
        language: window.navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const response = await visaApi.saveVisaRecord({
        Center: centerLabel,
        CenterId: center,
        VisaType: visaType,
        OnlineSubmissionDate: onlineSubmissionDate,
        BiometricDate: biometricDate,
        ConfirmationDate: confirmationDate,
        GdprConfirmed: gdprConfirmed,
        SendEmail: sendEmail,
        Email: email,
        Notes: notes,
        User: "Web",
        deviceInfo: deviceInfo,
      });
      if (response.success) {
        setNewVisa(false);
        setInProgress(false);
        setCenter(0);
        setCenterLabel("");
        setVisaType("ILR");
        setOnlineSubmissionDate("");
        setBiometricDate("");
        setConfirmationDate("");
        setGdprConfirmed(false);
        setSendEmail(false);
        setNotes("");
        handleFetch();
        toast.success("Record saved successfully!");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error("Error saving record");
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleSendEmailToDeveloper = () => {
    setShowSendEmailToDeveloper(true);
  };

  const getCarHeaderBackgroundColor = (confirmationDate) => {
    if (confirmationDate) {
      return "#0D7C66";
    }
    return "#060e3f";
  };

  const handleBuyMeACoffee = () => {
    window.open("https://buymeacoffee.com/orcasteam", "_blank");
  };
  return (
    <div>
      <ToastContainer position="top-center" newestOnTop={true} pauseOnHover theme="dark" />
      <Container className="py-4" style={{ marginBottom: "80px" }}>
        <Row>
          <h3
            style={{
              fontSize: window.innerWidth <= 768 ? "1.1rem" : "2rem",
              marginBottom: window.innerWidth <= 768 ? "0.5rem" : "1rem",
              whiteSpace: window.innerWidth <= 768 ? "nowrap" : "normal",
              overflow: window.innerWidth <= 768 ? "hidden" : "visible",
              textOverflow: window.innerWidth <= 768 ? "ellipsis" : "clip",
              padding: window.innerWidth <= 768 ? "0 5px" : "0",
            }}
          >
            {t("visa.header")}
          </h3>
        </Row>
        <Row
          className="py-4"
          style={{
            paddingTop: window.innerWidth <= 768 ? "0.5rem !important" : "1.5rem",
            paddingBottom: window.innerWidth <= 768 ? "0.5rem !important" : "1.5rem",
          }}
        >
          <Col xs={12} md={6} style={{ marginBottom: window.innerWidth <= 768 ? "0.5rem" : "1rem" }}>
            <Row className="align-items-center">
              <Col xs={4} md={3}>
                <label
                  style={{
                    marginTop: window.innerWidth <= 768 ? "5px" : "10px",
                    fontSize: window.innerWidth <= 768 ? "0.9rem" : "1rem",
                  }}
                >
                  {t("visa.center")}:
                </label>
              </Col>
              <Col xs={8} md={9}>
                <Select
                  name="center"
                  defaultValue={centerOptions.find((row) => row.value == center) || {}}
                  value={centerOptions.find((row) => row.value == center) || {}}
                  styles={{
                    ...customStyles,
                    control: (base) => ({
                      ...base,
                      minHeight: window.innerWidth <= 768 ? "35px" : "38px",
                      fontSize: window.innerWidth <= 768 ? "0.9rem" : "1rem",
                    }),
                  }}
                  onChange={(e) => {
                    setCenter(e.value);
                    setCenterLabel(e.label);
                  }}
                  options={centerOptions}
                  required
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} md={6} style={{ marginBottom: window.innerWidth <= 768 ? "0.5rem" : "1rem" }}>
            <Row className="align-items-center">
              <Col xs={4} md={3}>
                <label
                  style={{
                    marginTop: window.innerWidth <= 768 ? "5px" : "10px",
                    fontSize: window.innerWidth <= 768 ? "0.9rem" : "1rem",
                  }}
                >
                  {t("visa.visaType")}:
                </label>
              </Col>
              <Col xs={8} md={9}>
                <Select
                  name="visaType"
                  defaultValue={visaTypeOptions.find((row) => row.value == visaType)}
                  value={visaTypeOptions.find((row) => row.value == visaType)}
                  styles={{
                    ...customStyles,
                    control: (base) => ({
                      ...base,
                      minHeight: window.innerWidth <= 768 ? "35px" : "38px",
                      fontSize: window.innerWidth <= 768 ? "0.9rem" : "1rem",
                    }),
                  }}
                  onChange={(e) => {
                    setVisaType(e.value);
                  }}
                  options={visaTypeOptions}
                  required
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {newVisa ? (
          <Row>
            <Col md={4}>
              <Row>
                <Row>
                  <Col sm={5}>
                    <label>{t("visa.onlineSubmissionDate")}: </label>
                  </Col>
                  <Col sm={7}>
                    <input
                      type="date"
                      required
                      value={onlineSubmissionDate}
                      name="onlineSubmissionDate"
                      onChange={(e) => setOnlineSubmissionDate(e.target.value)}
                      className="form-control"
                    />
                  </Col>
                </Row>
              </Row>
            </Col>
            <Col md={4}>
              <Row>
                <Row>
                  <Col sm={5}>
                    <label>{t("visa.biometricDate")}: </label>
                  </Col>
                  <Col sm={7}>
                    <input
                      type="date"
                      required
                      value={biometricDate}
                      name="biometricDate"
                      onChange={(e) => setBiometricDate(e.target.value)}
                      className="form-control"
                    />
                  </Col>
                </Row>
              </Row>
            </Col>
            <Col md={4}>
              <Row>
                <Row>
                  <Col sm={5}>
                    <label>{t("visa.confirmationDate")}: </label>
                  </Col>
                  <Col sm={7}>
                    <input
                      type="date"
                      required
                      value={confirmationDate}
                      name="confirmationDate"
                      onChange={(e) => setConfirmationDate(e.target.value)}
                      className="form-control"
                    />
                  </Col>
                </Row>
              </Row>
            </Col>
          </Row>
        ) : (
          ""
        )}
        {newVisa ? (
          <Row className="py-4">
            <Col md={6}>
              <Row>
                <Col sm={3}>
                  <label>{t("visa.gdprConfirmed")}</label>
                </Col>
                <Col sm={9}>
                  <input
                    type="checkbox"
                    checked={gdprConfirmed}
                    required
                    name="gdprConfirmed"
                    onChange={(e) => setGdprConfirmed(e.target.checked)}
                    style={{ width: "30px", height: "30px", margin: "5px" }} // Added inline styles
                  />
                  <div className="checkbox-notes-container">{t("visa.gdprNotes")}</div>
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col sm={3}>
                  <label>{t("visa.sendEmail")}</label>
                </Col>
                <Col sm={9}>
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    required
                    name="sendEmail"
                    onChange={(e) => setSendEmail(e.target.checked)}
                    style={{ width: "30px", height: "30px", margin: "5px" }} // Added inline styles
                  />

                  <div className="checkbox-notes-container">{t("visa.sendEmailNotes")}</div>
                </Col>
              </Row>
              {sendEmail ? (
                <Row>
                  <Col sm={3}>
                    <label>{t("visa.email")}</label>
                  </Col>
                  <Col sm={9}>
                    <input
                      type="text"
                      value={email}
                      placeholder={t("visa.emailPlaceholder")}
                      required
                      name="email"
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-control"
                    />
                  </Col>
                </Row>
              ) : (
                ""
              )}
            </Col>
          </Row>
        ) : (
          ""
        )}
        {newVisa ? (
          <Row className="py-4">
            <Col md={12}>
              <Row>
                <Col sm={2}>
                  <label>{t("visa.notes")}</label>
                </Col>
                <Col sm={10}>
                  <input
                    type="textarea"
                    value={notes}
                    placeholder={t("visa.notesPlaceholder")}
                    required
                    name="notes"
                    onChange={(e) => setNotes(e.target.value)}
                    className="form-control"
                    style={{ width: "100%", minHeight: "50px" }} // Added inline style
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        ) : (
          ""
        )}
        <Row>
          <Col>
            <div
              className="Form2"
              style={{
                display: "flex",
                flexWrap: window.innerWidth <= 768 ? "nowrap" : "wrap",
                gap: window.innerWidth <= 768 ? "5px" : "10px",
                justifyContent: window.innerWidth <= 768 ? "space-between" : "flex-start",
                width: "100%",
              }}
            >
              {newVisa && !inProgress ? (
                <Button
                  variant="success"
                  onClick={handleSubmit}
                  style={{
                    backgroundColor: "#0D7C66",
                    ...(window.innerWidth <= 768 ? { flex: 1 } : { width: "30%" }),
                    height: window.innerWidth <= 768 ? "50px" : "60px",
                    padding: window.innerWidth <= 768 ? "5px" : "10px",
                    display: "flex",
                    flexDirection: window.innerWidth <= 768 ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 768 ? "12px" : "inherit",
                  }}
                >
                  {window.innerWidth <= 768 ? (
                    <>
                      <FaChessKnight size={20} color="#fff" />
                      {t("visa.buttons.save").split(" ")[0]}
                    </>
                  ) : (
                    <>
                      {t("visa.buttons.save")}
                      <FaChessKnight style={{ marginLeft: "10px" }} size={35} color="#fff" />
                    </>
                  )}
                </Button>
              ) : null}

              <Button
                variant="primary"
                onClick={handleFetch}
                style={{
                  ...(window.innerWidth <= 768 ? { flex: 1 } : { width: "30%" }),
                  height: window.innerWidth <= 768 ? "50px" : "60px",
                  padding: window.innerWidth <= 768 ? "5px" : "10px",
                  display: "flex",
                  flexDirection: window.innerWidth <= 768 ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: window.innerWidth <= 768 ? "12px" : "inherit",
                }}
              >
                {window.innerWidth <= 768 ? (
                  <>
                    <FaCat size={20} color="#fff" />
                    {t("visa.buttons.fetch").split(" ")[0]}
                  </>
                ) : (
                  <>
                    {t("visa.buttons.fetch")}
                    <FaCat style={{ marginLeft: "10px" }} size={35} color="#fff" />
                  </>
                )}
              </Button>

              {!newVisa ? (
                <Button
                  variant="success"
                  onClick={() => setNewVisa(!newVisa)}
                  style={{
                    backgroundColor: "#FF7F3E",
                    ...(window.innerWidth <= 768 ? { flex: 1 } : { width: "30%" }),
                    height: window.innerWidth <= 768 ? "50px" : "60px",
                    padding: window.innerWidth <= 768 ? "5px" : "10px",
                    display: "flex",
                    flexDirection: window.innerWidth <= 768 ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: window.innerWidth <= 768 ? "12px" : "inherit",
                  }}
                >
                  {window.innerWidth <= 768 ? (
                    <>
                      <FaHandPeace size={20} color="#fff" />
                      {t("visa.buttons.newVisa").split(" ")[0]}
                    </>
                  ) : (
                    <>
                      {t("visa.buttons.newVisa")}
                      <FaHandPeace style={{ marginLeft: "10px" }} size={35} color="#fff" />
                    </>
                  )}
                </Button>
              ) : null}

              <Button
                variant="success"
                onClick={() => setShowTableView(!showTableView)}
                style={{
                  backgroundColor: "#4A249D",
                  ...(window.innerWidth <= 768 ? { flex: 1 } : { width: "30%" }),
                  height: window.innerWidth <= 768 ? "50px" : "60px",
                  padding: window.innerWidth <= 768 ? "5px" : "10px",
                  display: "flex",
                  flexDirection: window.innerWidth <= 768 ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: window.innerWidth <= 768 ? "12px" : "inherit",
                }}
              >
                {window.innerWidth <= 768 ? (
                  <>
                    <FaCrow size={20} color="#fff" />
                    {showTableView ? "Card" : "Table"}
                  </>
                ) : (
                  <>
                    {showTableView ? t("visa.buttons.showCardView") : t("visa.buttons.showTableView")}
                    <FaCrow style={{ marginLeft: "10px" }} size={35} color="#fff" />
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <div className="table-responsive" style={{ marginTop: "20px" }}>
            {showTableView && (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th onClick={() => handleSort("Center")} style={{ cursor: "pointer" }}>
                      Center {sortConfig.key === "Center" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("VisaType")} style={{ cursor: "pointer" }}>
                      Visa Type {sortConfig.key === "VisaType" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("OnlineSubmissionDate")} style={{ cursor: "pointer" }}>
                      Online Submission Date{" "}
                      {sortConfig.key === "OnlineSubmissionDate" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("BiometricDate")} style={{ cursor: "pointer" }}>
                      Biometric Date{" "}
                      {sortConfig.key === "BiometricDate" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("ConfirmationDate")} style={{ cursor: "pointer" }}>
                      Confirmation Date{" "}
                      {sortConfig.key === "ConfirmationDate" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("TotalDuration")} style={{ cursor: "pointer" }}>
                      Total Duration{" "}
                      {sortConfig.key === "TotalDuration" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                    <th onClick={() => handleSort("Notes")} style={{ cursor: "pointer" }}>
                      Notes {sortConfig.key === "Notes" && (sortConfig.direction === "ascending" ? "▲" : "▼")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVisaRows &&
                    sortedVisaRows.map((guest, index) => (
                      <tr key={index}>
                        <td>{guest.Center}</td>
                        <td>{guest.VisaType}</td>
                        <td>{moment(guest.OnlineSubmissionDate).format("YYYY-MM-DD")}</td>
                        <td>{moment(guest.BiometricDate).format("YYYY-MM-DD")}</td>
                        <td>{guest.ConfirmationDate ? moment(guest.ConfirmationDate).format("YYYY-MM-DD") : ""}</td>
                        <td>
                          {guest.ConfirmationDate
                            ? moment(guest.ConfirmationDate).diff(moment(guest.OnlineSubmissionDate), "days")
                            : ""}
                        </td>
                        <td>{guest.Notes}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
            {sortedVisaRows &&
              !showTableView &&
              sortedVisaRows.length &&
              sortedVisaRows.map((guest, index) => (
                <Card key={index} style={{ border: "2px solid blue", borderRadius: "15px" }} className="mb-3">
                  <Card.Header
                    style={{
                      backgroundColor: getCarHeaderBackgroundColor(guest.ConfirmationDate),
                      margin: "8px",
                      borderRadius: "15px",
                      color: "#fff",
                      fontWeight: 600,
                      boxShadow: " 0 1px 3px rgba(0, 0, 0, 0.2)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "1rem" : "1.4em",
                        marginLeft: "10px",
                        marginRight: "10px",
                        float: "left",
                      }}
                    >
                      {guest.VisaType || "No Status Found"}
                    </p>
                    {guest.VisaType === "ILR" && !guest.ConfirmationDate && (
                      <FaChargingStation
                        color="#F2F9FF"
                        className="responsive-icon"
                        style={{
                          marginLeft: "auto",
                          fontSize: window.innerWidth <= 768 ? "1.2rem" : "1.4em",
                        }}
                      />
                    )}

                    {guest.VisaType === "ILR" && guest.ConfirmationDate ? (
                      <div>
                        <p
                          style={{
                            margin: "0px",
                            fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                            marginLeft: "30px",
                            marginRight: "10px",
                          }}
                        >
                          <span>{t("visa.confirmedCardHeaderMessage")}</span>
                        </p>
                      </div>
                    ) : (
                      <p
                        style={{
                          margin: "0px",
                          fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                          marginLeft: "10px",
                          marginRight: "10px",
                        }}
                      >
                        {t("visa.waitingCardHeaderMessage")}
                      </p>
                    )}
                    {guest.VisaType === "ILR" && guest.ConfirmationDate && (
                      <FaUserCheck
                        size={window.innerWidth <= 768 ? 25 : 40}
                        color="#F2F9FF"
                        className="responsive-icon"
                        style={{ marginLeft: "auto" }}
                      />
                    )}
                    {guest.VisaType === "ILR" && !guest.ConfirmationDate && (
                      <FaHandsPraying
                        size={window.innerWidth <= 768 ? 25 : 40}
                        color="#F2F9FF"
                        className="responsive-icon"
                        style={{ marginLeft: "auto" }}
                      />
                    )}
                  </Card.Header>
                  <Card.Body
                    key={index}
                    style={{
                      backgroundColor: " #4c548c",
                      margin: "8px",
                      padding: window.innerWidth <= 768 ? "10px" : "16px",
                      marginTop: "0px",
                      borderRadius: "15px",
                      color: "#fff",
                      fontWeight: 600,
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {t("visa.confirmationDate")}:{"   "}
                      {(guest.ConfirmationDate && moment(guest.ConfirmationDate).format("DD-MM-YYYY")) ||
                        "Waiting for confirmation"}
                    </p>
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {t("visa.totalDuration")}:{"    "}
                      {(guest.ConfirmationDate &&
                        moment(guest.ConfirmationDate).diff(moment(guest.OnlineSubmissionDate), "days")) ||
                        "N/A"}
                    </p>
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {t("visa.onlineSubmissionDate")}:{"   "}
                      {moment(guest.OnlineSubmissionDate).format("DD-MM-YYYY") || "N/A"}
                    </p>
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {t("visa.biometricDate")}: {moment(guest.BiometricDate).format("DD-MM-YYYY") || "N/A"}
                    </p>
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {t("visa.center")}:{"   "}
                      {guest.Center || "N/A"}
                    </p>
                    <p
                      style={{
                        margin: "0px",
                        fontSize: window.innerWidth <= 768 ? "0.9rem" : "1.4em",
                      }}
                    >
                      {guest.Notes || "N/A"}
                    </p>
                  </Card.Body>
                </Card>
              ))}
          </div>
        </Row>
        {!showSendEmailToDeveloper ? (
          <div className="footer">
            <button type="button" onClick={handleSendEmailToDeveloper} className="btn btn-success">
              <Container>
                <Row>
                  <Col md={9}>
                    <p>{t("visa.buttons.sendEmailToDeveloper")}</p>
                  </Col>
                  <Col md={3}>
                    <FaChess className="responsive-icon" size={50} color="#fff" />
                  </Col>
                </Row>
              </Container>
            </button>

            <button type="button" onClick={handleBuyMeACoffee} className="btn btn-primary">
              <Container>
                <Row>
                  <Col md={10}>
                    <p>{t("visa.buttons.buyMeACoffee")}</p>
                  </Col>
                  <Col md={2}>
                    <GiCoffeeCup className="responsive-icon" size={50} color="#fff" />
                  </Col>
                </Row>
              </Container>
            </button>
          </div>
        ) : (
          <div></div>
        )}
      </Container>
      <div>
        <Modal
          isOpen={showSendEmailToDeveloper}
          onRequestClose={() => setShowSendEmailToDeveloper(false)}
          style={{
            overlay: {
              zIndex: 99,
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(208, 211, 192, 0.75)",
              backgroundOpacity: "0.8",
              borderRadius: "40px",
            },
            content: {
              zIndex: 99,
              color: "#10375c",
              borderRadius: "20px",
              margin: "0 auto",
              padding: "0",
              width: getWithPercentage(),
            },
          }}
        >
          <SendEmail t={t} toast={toast} setShowSendEmailToDeveloper={setShowSendEmailToDeveloper} />
          <button
            onClick={() => setShowSendEmailToDeveloper(false)}
            className="btn btn-secondary"
            style={{
              maxWidth: "90%",
              margin: "0 auto",
              display: "block",
              marginTop: "10px",
              width: "70%",
              backgroundColor: "#a8786a",
              color: "#fff",
              marginBottom: "30px",
            }}
          >
            Close
          </button>
        </Modal>
      </div>
    </div>
  );
};

export default MyVisaScreen;
